import {
    computed,
    DestroyRef,
    inject,
    Injectable,
    InjectionToken,
    Signal,
    signal,
    untracked,
    WritableSignal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    concat,
    defer,
    EMPTY,
    finalize,
    firstValueFrom,
    forkJoin,
    isObservable,
    merge,
    Observable,
    of,
    ReplaySubject,
    retry,
    share,
    Subject,
    tap,
    throwError,
    timer,
} from "rxjs";
import {
    catchError,
    concatMap,
    defaultIfEmpty,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
} from "rxjs/operators";
import { LoadingTranslationsRegistry } from "./loading-translations-registry";
import { MissingTranslationHandler } from "./missing-translation-handler";
import { TranslateCompiler } from "./translate.compiler";
import { TranslateLoader } from "./translate.loader";
import { TranslateParser, type InterpolationContext } from "./translate.parser";
import {
    FallbackLangChangeEvent,
    InterpolatableTranslation,
    InterpolatableTranslationObject,
    InterpolationParameters,
    ITranslateService,
    LangChangeEvent,
    Language,
    StrictTranslation,
    Translation,
    TranslationChangeEvent,
    TranslationObject,
} from "./translate.service.interface";
import { DeepReadonly, TranslateStore } from "./translate.store";
import { insertValue, isArray, isDefinedAndNotNull, isDict, isString } from "./util";

/** Retry policy for a failed language load. */
export interface TranslateRetryConfig {
    /** Maximum retries after the initial attempt. Zero or negative values disable retries. */
    count: number;
    /**
     * Delay in milliseconds (default 100). The first retry has retryAttempt = 1.
     * Values must be finite and between 0 and 2147483647; invalid values fail with RangeError.
     * Exceptions from this callback or shouldRetry terminate the load with that exception.
     */
    delay?: number | ((error: unknown, retryAttempt: number) => number);
    /** Return false to immediately propagate the original error. Defaults to retrying all errors. */
    shouldRetry?: (error: unknown) => boolean;
}

/**
 * Configuration object for the translation service.
 *
 * Provides options to customize translation behavior, including setting the primary language,
 * specifying a fallback language, and other deprecated flags for legacy support.
 */
export interface TranslateServiceConfig {
    lang?: Language;
    langs?: Language[];
    fallbackLang?: Language | null;
    /** Ordered fallback chain. Takes precedence over fallbackLang, including when empty. */
    fallbackLangs?: readonly Language[];
    /**
     * Automatic retries for a failed language load (default 0).
     * A number uses a fixed 100 ms delay; a policy customizes timing and error filtering.
     * With `@ngx-translate/http-loader`, requires `failOnError: true` on the loader.
     * Combining loader-level and service-level retries can multiply attempts.
     */
    retry?: number | TranslateRetryConfig;
    isRoot: boolean;
}

export const TRANSLATE_SERVICE_CONFIG = new InjectionToken<TranslateServiceConfig>(
    "TRANSLATE_CONFIG",
);

declare interface Window {
    navigator: {
        languages?: string[];
        language?: string;
        browserLanguage?: string;
        userLanguage?: string;
    };
}

declare const window: Window;

const makeObservable = <T>(value: T | Observable<T>): Observable<T> => {
    return isObservable(value) ? value : of(value);
};

const RETRY_DELAY_MS = 100;
const MAX_RETRY_DELAY_MS = 2 ** 31 - 1;

interface TranslationLookupResult {
    translation: InterpolatableTranslation;
    context: InterpolationContext;
}

@Injectable()
export class TranslateService implements ITranslateService {
    protected readonly loadingTranslations = new LoadingTranslationsRegistry();
    // Retain cancellation across registry clears so reset/reload cannot hide older loads.
    private readonly loadCancellation = new Map<Language, ReplaySubject<void>>();
    private languageRequestSequence = 0;
    private lastFallbackRequest = 0;
    private readonly languageRequestIds = new WeakMap<LangChangeEvent, number>();
    private readonly deletedLanguageRequests = new Map<Language, number>();
    protected lastUseLanguage: Language | null = null;
    private readonly retry: number | TranslateRetryConfig;

    protected currentLoader = inject(TranslateLoader);
    protected compiler = inject(TranslateCompiler);
    protected parser = inject(TranslateParser);
    protected missingTranslationHandler = inject(MissingTranslationHandler);
    protected store: TranslateStore = inject(TranslateStore);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly parent: TranslateService | null;

    protected get isRoot(): boolean {
        return this.parent === null;
    }

    protected _onLangChange = new Subject<LangChangeEvent>();
    protected _onFallbackLangChange = new Subject<FallbackLangChangeEvent>();
    protected _currentLang: WritableSignal<Language | null> = signal(null);
    protected _fallbackLang: WritableSignal<Language | null> = signal(null);
    private readonly _additionalFallbackLangs = signal<readonly Language[]>([]);
    private readonly _fallbackLangs = computed(() => {
        const first = this._fallbackLang();
        return first === null ? [] : [first, ...this._additionalFallbackLangs()];
    });
    private _onTranslationRefresh: Observable<void> | null = null;

    // Downward-inheritance: `true` if THIS service has loads in flight, OR any
    // ancestor does. Walks the parent chain via the public `parent.isLoading()`
    // getter (not by reaching into `parent.loadingTranslations`) so the
    // encapsulation boundary holds. Angular signals re-collect dependencies on
    // each evaluation; short-circuit is safe — the only signal whose flip
    // could change the result is the one returned by short-circuit, and it IS
    // tracked. Parent chain is acyclic (DI tree is acyclic; `skipSelf` only
    // walks up), so no cycle guard needed.
    //
    // Set-based composition at each level: `loadingTranslations.hasAny()`
    // stays true while ANY language has an in-flight load on this service,
    // flips false only when the last entry is cleared.
    private _isLoading: Signal<boolean> = computed(
        () => this.loadingTranslations.hasAny() || (this.parent?.isLoading() ?? false),
    );

    /**
     * Returns the root of this service's hierarchy — the topmost service in
     * the `getParent()` chain. For an isolated subtree, returns the subtree's
     * root (since `parent === null` at the isolation boundary).
     *
     * A root service returns itself. Equivalent to walking `getParent()` until
     * it returns `null`, but provided as a convenience.
     */
    public getRoot(): TranslateService {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let svc: TranslateService = this;
        while (svc.parent) svc = svc.parent;
        return svc;
    }

    /**
     * Returns the service this one inherits translations from, or `null` if
     * this is a root (a top-level service or an isolated subtree root).
     *
     * A `null` return means the service is the terminus of its translation
     * fallback chain — equivalent to "is this a root?".
     */
    public getParent(): TranslateService | null {
        return this.parent;
    }

    /**
     * The language most recently requested via `use()`. Always read from the
     * root, because `use()` delegates to the root (`parent!.use(lang)`) and only
     * the root ever assigns `lastUseLanguage`. A child's own `lastUseLanguage`
     * stays `null`, so reading it directly would make `get()` miss the child's
     * in-flight load. `null` until the first `use()` runs.
     */
    protected getActiveRequestedLang(): Language | null {
        return this.getRoot().lastUseLanguage;
    }

    protected hasTranslationInChain(lang: Language): boolean {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        for (let svc: TranslateService | null = this; svc; svc = svc.parent) {
            if (svc.store.hasTranslationFor(lang)) return true;
        }
        return false;
    }

    protected chainTranslationChange$(): Observable<TranslationChangeEvent> {
        const streams: Observable<TranslationChangeEvent>[] = [];
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        for (let svc: TranslateService | null = this; svc; svc = svc.parent) {
            streams.push(svc.store.translationChange$);
        }
        return streams.length === 1 ? streams[0] : merge(...streams);
    }

    /**
     * An Observable to listen to translation change events
     * onTranslationChange.subscribe((params: TranslationChangeEvent) => {
     *     // do something
     * });
     */
    public get onTranslationChange(): Observable<TranslationChangeEvent> {
        return this.store.translationChange$;
    }

    /**
     * An Observable to listen to lang change events
     * onLangChange.subscribe((params: LangChangeEvent) => {
     *     // do something
     * });
     */
    get onLangChange(): Observable<LangChangeEvent> {
        if (this.isRoot) {
            return this._onLangChange.asObservable();
        }
        return this.parent ? this.parent.onLangChange : EMPTY;
    }

    /**
     * An Observable to listen to fallback lang change events
     * onFallbackLangChange.subscribe((params: FallbackLangChangeEvent) => {
     *     // do something
     * });
     */
    get onFallbackLangChange(): Observable<FallbackLangChangeEvent> {
        if (this.isRoot) {
            return this._onFallbackLangChange.asObservable();
        }
        return this.parent ? this.parent.onFallbackLangChange : EMPTY;
    }

    /**
     * A combined Observable that emits whenever translations might need to be refreshed.
     * This includes: language changes, translation updates for the current or fallback language,
     * and fallback language changes.
     */
    get onTranslationRefresh(): Observable<void> {
        if (!this._onTranslationRefresh) {
            const refresh$ = merge(
                this.onTranslationChange.pipe(
                    filter(
                        (event) =>
                            event.lang === this.getCurrentLang() ||
                            this.getFallbackLangs().includes(event.lang),
                    ),
                ),
                this.onLangChange,
                this.onFallbackLangChange,
            ).pipe(map(() => void 0));

            if (this.isRoot) {
                this._onTranslationRefresh = refresh$;
            } else {
                this._onTranslationRefresh = this.parent
                    ? merge(refresh$, this.parent.onTranslationRefresh)
                    : refresh$;
            }
        }
        return this._onTranslationRefresh;
    }

    constructor() {
        const config: TranslateServiceConfig = {
            isRoot: true,
            fallbackLang: null,

            ...inject<TranslateServiceConfig>(TRANSLATE_SERVICE_CONFIG, {
                optional: true,
            }),
        };

        // parent === null exactly means "I am a root" (including isolated-subtree roots).
        // isRoot is now a getter derived from this single source of truth.
        this.parent = config.isRoot
            ? null
            : inject(TranslateService, { optional: true, skipSelf: true });

        if (typeof config.retry === "object") {
            const { count, delay, shouldRetry } = config.retry;
            this.retry = {
                count,
                delay: typeof delay === "function" ? delay.bind(config.retry) : delay,
                shouldRetry: shouldRetry?.bind(config.retry),
            };
        } else {
            this.retry = Math.max(0, config.retry ?? 0);
        }

        const destroyRef = inject(DestroyRef);

        if (this.isRoot) {
            if (config.langs?.length) {
                this.addLangs(config.langs);
            }
            if (config.lang) {
                this.use(config.lang);
            }
            if (config.fallbackLangs !== undefined) {
                this.setFallbackLangs(config.fallbackLangs);
            } else if (config.fallbackLang) {
                this.setFallbackLang(config.fallbackLang);
            }
        } else {
            // Child services should initially load the root's current and fallback languages.
            // Loader failures are warned contextually here — the internal no-op subscribe
            // inside loadAndCompileTranslations would otherwise swallow them silently.
            // Best-effort: takeUntilDestroyed tears down the subscription on host destroy,
            // so a destroy-then-error race silently drops the warn.
            const currentLang = this.getCurrentLang();
            if (currentLang) {
                this.loadOrExtendLanguage(currentLang)
                    ?.pipe(takeUntilDestroyed(destroyRef))
                    .subscribe({
                        error: (err) => {
                            console.warn(
                                `@ngx-translate/core: child failed to load "${currentLang}". Cause:`,
                                err,
                            );
                        },
                    });
            }
            for (const fallbackLang of this.getFallbackLangs()) {
                // Dedup guard: currentLang === fallbackLang means both branches would
                // resolve to the same in-flight observable (via the loading-translations
                // registry's get-or-create) and double-log on error.
                if (fallbackLang && fallbackLang !== currentLang) {
                    this.loadOrExtendLanguage(fallbackLang)
                        ?.pipe(takeUntilDestroyed(destroyRef))
                        .subscribe({
                            error: (err) => {
                                console.warn(
                                    `@ngx-translate/core: child failed to load "${fallbackLang}". Cause:`,
                                    err,
                                );
                            },
                        });
                }
            }
        }

        // Child services should load translations when the language changes on the root
        this.onLangChange.pipe(takeUntilDestroyed(destroyRef)).subscribe((event) => {
            if (!this.isRoot && this.shouldLoadLanguage(event)) {
                this.loadOrExtendLanguage(event.lang)
                    ?.pipe(takeUntilDestroyed(destroyRef))
                    .subscribe({
                        error: (err) => {
                            console.warn(
                                `@ngx-translate/core: child failed to load "${event.lang}". Cause:`,
                                err,
                            );
                        },
                    });
            }
        });

        this.onFallbackLangChange.pipe(takeUntilDestroyed(destroyRef)).subscribe((event) => {
            if (this.isRoot) return;
            for (const lang of event.langs ?? [event.lang]) {
                if (this.shouldLoadLanguage(event, lang)) {
                    this.loadOrExtendLanguage(lang)
                        ?.pipe(takeUntilDestroyed(destroyRef))
                        .subscribe({
                            error: (err) => {
                                console.warn(
                                    `@ngx-translate/core: child failed to load "${lang}". Cause:`,
                                    err,
                                );
                            },
                        });
                }
            }
        });

        // Complete this service's Subjects when its injector tears down.
        // Root singletons live as long as the app, but child services on
        // lazy routes would otherwise pin their Subjects until GC.
        destroyRef.onDestroy(() => {
            this._onLangChange.complete();
            this._onFallbackLangChange.complete();
        });
    }

    private shouldLoadLanguage(event: LangChangeEvent, lang = event.lang): boolean {
        const deletedAt = this.deletedLanguageRequests.get(lang);
        const requestId = this.getRoot().languageRequestIds.get(event);
        return deletedAt === undefined || requestId === undefined || requestId > deletedAt;
    }

    /**
     * Sets the fallback language to use if a translation is not found in the
     * current language
     */
    public setFallbackLang(lang: Language): Observable<InterpolatableTranslationObject> {
        if (!this.isRoot) {
            return this.parent!.setFallbackLang(lang);
        }
        return this.setFallbackLangs([lang]).pipe(map((translations) => translations[0]));
    }

    /**
     * Loads an ordered fallback chain. An empty array disables fallback.
     * Emits the loaded dictionaries in order; the most recent request wins.
     */
    public setFallbackLangs(
        languages: readonly Language[],
    ): Observable<InterpolatableTranslationObject[]> {
        if (!this.isRoot) {
            return this.parent!.setFallbackLangs(languages);
        }
        // Commands must not register dependencies when called from an effect.
        return untracked(() => {
            const langs = [...new Set(languages)];
            const requestId = ++this.languageRequestSequence;
            this.lastFallbackRequest = requestId;
            if (this._fallbackLang() === null) {
                this.setFallbackLanguageState(langs);
            }
            const pending = langs.length
                ? forkJoin(
                      langs.map((lang) =>
                          (
                              this.loadOrExtendLanguage(lang) ??
                              of(this.store.getTranslations(lang))
                          ).pipe(take(1)),
                      ),
                  )
                : of([]);
            pending.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: (translations) => {
                    if (
                        this.lastFallbackRequest !== requestId ||
                        langs.some(
                            (lang) => (this.deletedLanguageRequests.get(lang) ?? -1) >= requestId,
                        )
                    )
                        return;
                    this.setFallbackLanguageState(langs);
                    const event: FallbackLangChangeEvent = {
                        lang: langs[0] ?? "",
                        translations: translations[0] ?? {},
                        ...(langs.length !== 1 && { langs: this.getFallbackLangs() }),
                    };
                    this.languageRequestIds.set(event, requestId);
                    this._onFallbackLangChange.next(event);
                },
                error: (err) => {
                    console.warn(
                        `@ngx-translate/core: failed to load fallback "${langs.join(", ")}". Cause:`,
                        err,
                    );
                },
            });
            return pending;
        });
    }

    private setFallbackLanguageState(langs: readonly Language[]): void {
        this._additionalFallbackLangs.set(langs.slice(1));
        this._fallbackLang.set(langs[0] ?? null);
    }

    /**
     * Signal that is `true` while one or more language loads are in flight at
     * this service or any of its ancestors in the service hierarchy.
     *
     * Loading scope propagates DOWNWARD: a load triggered at the root marks
     * the root and all descendants as loading. A load triggered at a child
     * (e.g. a lazy-route bootstrap fetching its translations) marks only that
     * child's subtree. Siblings and ancestors are unaffected by a descendant's
     * loads.
     *
     * Drive a spinner by reading it from the service injected at the scope
     * where the spinner should live: root for an app-shell spinner, the
     * nearest child for a local spinner inside a lazy-loaded subtree.
     */
    public get isLoading(): Signal<boolean> {
        return this._isLoading;
    }

    /**
     * Changes the lang currently used
     */
    public use(lang: Language): Observable<InterpolatableTranslationObject> {
        if (!this.isRoot) {
            return this.parent!.use(lang);
        }

        // use() is a command, not a reactive read. Run its synchronous body in
        // `untracked` so the signal reads/writes it performs (_currentLang, the
        // loading registry, the store's translations) never register as
        // dependencies of a caller's reactive context. Without this, the
        // idiomatic `effect(() => translate.use(lang()))` tracks those signals
        // and the same call mutates them, re-triggering the effect (issue
        // #1633). Async loader callbacks fire outside this scope and outside any
        // reactive consumer, so they need no guard.
        return untracked(() => {
            const requestId = ++this.languageRequestSequence;
            // Snapshot prior state so we can roll back if the loader fails.
            const prevLang = this._currentLang();
            const prevLastUseLang = this.lastUseLanguage;

            // Remember the language that was called — used by changeLang() to discard
            // late-arriving completions from superseded calls.
            this.lastUseLanguage = lang;

            if (!this._currentLang()) {
                // on init set the currentLang immediately, but do not emit a change yet
                this._currentLang.set(lang);
            }

            const pending = this.loadOrExtendLanguage(lang);
            if (!isObservable(pending)) {
                // Defensive: loadOrExtendLanguage is typed `Observable | undefined`.
                // The undefined branch means "nothing to load" — synchronously activate.
                this.changeLang(lang, requestId);
                return of(this.store.getTranslations(lang));
            }

            pending.pipe(take(1)).subscribe({
                next: () => {
                    this.changeLang(lang, requestId);
                },
                error: (err) => {
                    // A policy load may have recovered in another subscriber's error handler.
                    if (
                        this.lastUseLanguage === lang &&
                        (typeof this.retry === "number" || !this.store.hasTranslationFor(lang))
                    ) {
                        this._currentLang.set(prevLang);
                        this.lastUseLanguage = prevLastUseLang;
                    }
                    console.warn(
                        `@ngx-translate/core: failed to load "${lang}". ` +
                            `currentLang was NOT changed; remains ` +
                            `"${prevLang ?? "null"}". Cause:`,
                        err,
                    );
                },
            });
            return pending;
        });
    }

    /**
     * Retrieves the given translations
     */
    protected loadOrExtendLanguage(
        lang: Language,
    ): Observable<InterpolatableTranslationObject> | undefined {
        // if this language is unavailable, ask for it
        if (!this.store.hasTranslationFor(lang)) {
            return this.loadAndCompileTranslations(lang);
        }

        return of(this.store.getTranslations(lang));
    }

    /**
     * @returns The loaded translations for the given language
     */
    public getTranslations(language: Language): DeepReadonly<InterpolatableTranslationObject> {
        return this.store.getTranslations(language);
    }

    /**
     * Changes the current lang
     */
    protected changeLang(lang: Language, requestId?: number): void {
        if (lang !== this.lastUseLanguage) {
            // received new language data,
            // but this was not the one requested last
            return;
        }

        this._currentLang.set(lang);
        const event = { lang: lang, translations: this.store.getTranslations(lang) };
        if (requestId !== undefined) {
            this.languageRequestIds.set(event, requestId);
        }
        this._onLangChange.next(event);
    }

    public getCurrentLang(): Language | null {
        return this.isRoot ? this._currentLang() : (this.parent?.getCurrentLang() ?? null);
    }

    /**
     * Loads translations for `lang` via the configured `TranslateLoader`,
     * compiles them, and stores the result. Tracking via the protected
     * `loadingTranslations` registry happens automatically.
     *
     * Subclasses that override this method bypass `isLoading` tracking
     * unless they call `this.loadingTranslations.set(lang, obs)` and arrange
     * a token-aware finalize (`this.loadingTranslations.clearIfOwner(lang, obs)`)
     * from the override.
     */
    protected loadAndCompileTranslations(
        lang: Language,
    ): Observable<InterpolatableTranslationObject> {
        const existing = this.loadingTranslations.get(lang);
        if (existing) {
            return existing;
        }

        const retryConfig: TranslateRetryConfig =
            typeof this.retry === "number" ? { count: this.retry } : this.retry;
        let cancellation = this.loadCancellation.get(lang);
        if (!cancellation) {
            cancellation = new ReplaySubject<void>(1);
            this.loadCancellation.set(lang, cancellation);
        }

        /*
         * Only the loader source is resubscribed: intermediate failures never
         * reach the map/tap/finalize chain below, so the registry entry stays
         * in-flight (isLoading stays true) until the terminal outcome.
         */
        // Register before invoking custom loader code, which may delete the language.
        const source$ = defer(() => {
            const source = this.currentLoader.getTranslation(lang);
            return retryConfig.count > 0
                ? source.pipe(
                      retry({
                          count: retryConfig.count,
                          delay: (error: unknown, retryAttempt) => {
                              if (retryConfig.shouldRetry && !retryConfig.shouldRetry(error)) {
                                  return throwError(() => error);
                              }
                              const delay = retryConfig.delay ?? RETRY_DELAY_MS;
                              const delayMs =
                                  typeof delay === "function" ? delay(error, retryAttempt) : delay;
                              if (
                                  !Number.isFinite(delayMs) ||
                                  delayMs < 0 ||
                                  delayMs > MAX_RETRY_DELAY_MS
                              ) {
                                  throw new RangeError(
                                      "@ngx-translate/core: retry delay must be a finite number between 0 and 2147483647 milliseconds.",
                                  );
                              }
                              return timer(delayMs);
                          },
                      }),
                  )
                : source;
        });

        let completed = false;
        const shared$ = source$.pipe(
            map((res: TranslationObject) => this.compiler.compileTranslations(res, lang)),
            // Invalidate writes before deletion events can invoke user code.
            filter(() => this.loadCancellation.get(lang) === cancellation),
            tap({
                next: (compiled: InterpolatableTranslationObject) => {
                    this.store.setTranslations(lang, compiled, false);
                    // Clear before notifying subscribers so isLoading reflects the completed load.
                    this.loadingTranslations.clearIfOwner(lang, translations$);
                },
                error: () => {
                    if (typeof this.retry !== "number") {
                        // A fresh service call inside an error handler must not reuse this failed load.
                        this.loadingTranslations.clearIfOwner(lang, translations$);
                    }
                },
            }),
            // Deletion events can trigger a source error before cancellation is notified.
            catchError((error: unknown) =>
                this.loadCancellation.get(lang) === cancellation ? throwError(() => error) : EMPTY,
            ),
            tap({
                complete: () => {
                    completed = this.loadCancellation.get(lang) === cancellation;
                },
            }),
            takeUntil(cancellation),
            // Token-aware clear: if `resetLang` + `reloadLang` raced between
            // set() and finalize(), this load's finalize must NOT clobber the
            // newer load's entry. clearIfOwner compares by reference identity.
            finalize(() => this.loadingTranslations.clearIfOwner(lang, translations$)),
            // Policies cache errors; numeric/default retries retain legacy resubscription behavior.
            share({
                connector: () => new ReplaySubject<InterpolatableTranslationObject>(1),
                resetOnError: typeof this.retry === "number",
                resetOnComplete: false,
                resetOnRefCountZero: true,
            }),
        );
        // Preserve completed snapshots, but never replay or restart a cancelled load.
        const translations$ = defer(() =>
            completed || this.loadCancellation.get(lang) === cancellation ? shared$ : EMPTY,
        );

        this.loadingTranslations.set(lang, translations$);

        // Trigger loading if nobody subscribes from outside. The error callback
        // is intentionally a no-op: use() and setFallbackLang() already emit a
        // console.warn on loader failure for their own paths. Warning here
        // would double-log for those callers. Bound to the service lifetime so a
        // non-completing/hot custom loader cannot pin this subscription (and the
        // upstream loader subscription) past service teardown.
        translations$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            error: () => {},
        });

        return translations$;
    }

    /**
     * Manually sets an object of translations for a given language,
     * passing it through the configured {@link TranslateCompiler} first.
     *
     * If you already have translations in their final compiled form
     * (e.g. interpolation functions produced at build time), use
     * {@link setCompiledTranslation} instead — it stores the data
     * directly and skips the compiler.
     */
    public setTranslation(
        lang: Language,
        translations: TranslationObject,
        shouldMerge = false,
    ): void {
        const interpolatableTranslations: InterpolatableTranslationObject =
            this.compiler.compileTranslations(translations, lang);
        this.store.setTranslations(lang, interpolatableTranslations, shouldMerge);
    }

    /**
     * Stores an already-compiled translation object for the given language,
     * bypassing the configured {@link TranslateCompiler}.
     *
     * Use this when you have translations in their final, interpolator-ready
     * form — e.g. interpolation functions produced at build time. For raw
     * translations that still need to go through the compiler, use
     * {@link setTranslation} instead.
     */
    public setCompiledTranslation(
        lang: Language,
        translations: InterpolatableTranslationObject,
        shouldMerge = false,
    ): void {
        this.store.setTranslations(lang, translations, shouldMerge);
    }

    public getLangs(): readonly Language[] {
        return this.store.getLanguages();
    }

    /**
     * Add available languages
     */
    public addLangs(languages: Language[]): void {
        this.store.addLanguages(languages);
    }

    protected getParsedResultForKey(
        key: string,
        interpolateParams?: InterpolationParameters,
        lang?: Language,
    ): StrictTranslation | Observable<StrictTranslation> {
        let lookup: TranslationLookupResult | undefined;
        const translation = this.getTextToInterpolate(key, lang, (result) => {
            lookup = result;
        });

        if (isDefinedAndNotNull(translation)) {
            // An override must report context for the value it returns.
            const context = lookup?.translation === translation ? lookup.context : { key };
            return this.runInterpolation(translation, interpolateParams, context);
        }

        const handler = this.getMissingTranslationHandler();
        const res = handler.handle({
            key,
            translateService: this,
            ...(interpolateParams !== undefined && { interpolateParams }),
        });

        return res !== undefined ? res : key;
    }

    protected getMissingTranslationHandler(): MissingTranslationHandler {
        return this.missingTranslationHandler;
    }

    /**
     * Gets the fallback language. null if none is defined
     */
    public getFallbackLang(): Language | null {
        return this.isRoot ? this._fallbackLang() : (this.parent?.getFallbackLang() ?? null);
    }

    /** Returns the ordered fallback chain, or an empty array when none is configured. */
    public getFallbackLangs(): readonly Language[] {
        return this.fallbackLangs();
    }

    /** Forward onLookup when overriding this method to preserve parser language context. */
    protected getTextToInterpolate(
        key: string,
        lang?: Language,
        onLookup?: (lookup: TranslationLookupResult) => void,
    ): InterpolatableTranslation | undefined {
        if (lang) {
            const res = this.store.getTranslationValue(lang, key);
            if (res !== undefined) {
                onLookup?.({ translation: res, context: { key, lang } });
                return res;
            }
            return this.parent?.getTextToInterpolate(key, lang, onLookup);
        }

        const currentLang = this.getCurrentLang();

        // 1. Try own store (currentLang)
        let res: InterpolatableTranslation | undefined;
        let resLang: Language | undefined;
        if (currentLang) {
            res = this.store.getTranslationValue(currentLang, key);
            resLang = currentLang;
        }

        // 2. Try each fallback in order; null values also trigger fallback.
        for (const fallbackLang of this.getFallbackLangs()) {
            if (isDefinedAndNotNull(res)) break;
            if (fallbackLang !== currentLang) {
                res = this.store.getTranslationValue(fallbackLang, key);
                resLang = fallbackLang;
            }
        }

        if (res !== undefined) {
            onLookup?.({
                translation: res,
                context: resLang === undefined ? { key } : { key, lang: resLang },
            });
            return res;
        }

        return this.parent?.getTextToInterpolate(key, lang, onLookup);
    }

    protected runInterpolation(
        translations: InterpolatableTranslation,
        interpolateParams?: InterpolationParameters,
        context?: InterpolationContext,
    ): StrictTranslation {
        if (!isDefinedAndNotNull(translations)) {
            return;
        }

        if (isArray(translations)) {
            return this.runInterpolationOnArray(translations, interpolateParams, context);
        }

        if (isDict(translations)) {
            return this.runInterpolationOnDict(translations, interpolateParams, context);
        }

        return this.parser.interpolate(translations, interpolateParams, context);
    }

    protected runInterpolationOnArray(
        translations: InterpolatableTranslation,
        interpolateParams: InterpolationParameters | undefined,
        context?: InterpolationContext,
    ) {
        return (translations as StrictTranslation[]).map((translation) =>
            this.runInterpolation(translation, interpolateParams, context),
        );
    }

    protected runInterpolationOnDict(
        translations: InterpolatableTranslationObject,
        interpolateParams: InterpolationParameters | undefined,
        context?: InterpolationContext,
    ) {
        const result: TranslationObject = {};
        for (const key in translations) {
            const res = this.runInterpolation(translations[key], interpolateParams, context);
            if (res !== undefined) {
                result[key] = res;
            }
        }
        return result;
    }

    /**
     * Returns the parsed result of the translations
     */
    public getParsedResult(
        key: string | string[],
        interpolateParams?: InterpolationParameters,
        lang?: Language,
    ): StrictTranslation | Observable<StrictTranslation> {
        return key instanceof Array
            ? this.getParsedResultForArray(key, interpolateParams, lang)
            : this.getParsedResultForKey(key, interpolateParams, lang);
    }

    protected getParsedResultForArray(
        key: string[],
        interpolateParams: InterpolationParameters | undefined,
        lang?: Language,
    ) {
        const result: Record<string, StrictTranslation | Observable<StrictTranslation>> = {};

        let observables = false;
        for (const k of key) {
            result[k] = this.getParsedResultForKey(k, interpolateParams, lang);
            observables = observables || isObservable(result[k]);
        }

        if (!observables) {
            return result as TranslationObject;
        }

        const sources: Observable<StrictTranslation>[] = key.map((k) => makeObservable(result[k]));
        return forkJoin(sources).pipe(
            map((arr: StrictTranslation[]) => {
                const obj: TranslationObject = {};
                arr.forEach((value: StrictTranslation, index: number) => {
                    obj[key[index]] = value;
                });
                return obj;
            }),
        );
    }

    /**
     * Gets the translated value of a key (or an array of keys)
     * @returns the translated key, or an object of translated keys
     */
    public get(
        key: string | string[],
        interpolateParams?: InterpolationParameters,
        lang?: Language,
    ): Observable<Translation> {
        if (!isDefinedAndNotNull(key) || !key.length) {
            return of("");
        }

        // check if we are loading a new translation to use.
        // Use the ROOT's last-requested language (not this service's own
        // lastUseLanguage, which a child never sets) so a child's in-flight
        // load is found in its registry; fall back to the current language when
        // no use() has run yet.
        const effectiveLang = lang ?? this.getActiveRequestedLang() ?? this.getCurrentLang();
        const observed = new Set<Observable<InterpolatableTranslationObject>>();
        const waitForTranslations = (): Observable<Translation> => {
            const languages = new Set([
                ...(effectiveLang ? [effectiveLang] : []),
                ...(lang ? [] : this.getFallbackLangs()),
            ]);
            const pending: Observable<unknown>[] = [];
            // Ancestors can still be loading before their language events reach this child.
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            for (let service: TranslateService | null = this; service; service = service.parent) {
                for (const language of languages) {
                    const load = service.loadingTranslations.get(language);
                    // Empty completion can notify us before the registry's finalize runs.
                    if (load && !observed.has(load)) {
                        observed.add(load);
                        pending.push(load.pipe(take(1), defaultIfEmpty(undefined)));
                    }
                }
            }
            return pending.length
                ? forkJoin(pending).pipe(concatMap(waitForTranslations))
                : makeObservable(this.getParsedResult(key, interpolateParams, lang));
        };
        return waitForTranslations();
    }

    /**
     * Gets the translated value of a key (or an array of keys) as a Promise,
     * for usage in async functions without wrapping `get` into `firstValueFrom`.
     * @returns a promise resolving with the translated key, or an object of translated keys
     */
    public getAsync(
        key: string | string[],
        interpolateParams?: InterpolationParameters,
        lang?: Language,
    ): Promise<Translation> {
        return firstValueFrom(this.get(key, interpolateParams, lang));
    }

    /**
     * Returns a stream of translated values of a key (or an array of keys) which updates
     * whenever the translation changes.
     * @returns A stream of the translated key, or an object of translated keys
     */
    public getStreamOnTranslationChange(
        key: string | string[],
        interpolateParams?: InterpolationParameters,
        lang?: Language,
    ): Observable<Translation> {
        if (!isDefinedAndNotNull(key) || !key.length) {
            throw new Error(`Parameter "key" is required and cannot be empty`);
        }

        return concat(
            defer(() => this.get(key, interpolateParams, lang)),
            this.onTranslationChange.pipe(
                switchMap(() => {
                    const res = this.getParsedResult(key, interpolateParams, lang);
                    return makeObservable(res);
                }),
            ),
        );
    }

    /**
     * Returns a stream of translated values of a key (or an array of keys) which updates
     * whenever the language changes, the requested language's translations are
     * (re)loaded, or the explicitly-requested `lang` argument's translations change.
     *
     * Without `lang`: re-emits on `onLangChange` (active-language switches via
     * {@link use}). With `lang`: also re-emits when translations for that specific
     * language are loaded or updated via `store.translationChange$`, so an
     * explicit `stream("KEY", undefined, "de")` updates once "de" finishes
     * loading.
     *
     * @returns A stream of the translated key, or an object of translated keys
     */
    public stream(
        key: string | string[],
        interpolateParams?: InterpolationParameters,
        lang?: Language,
    ): Observable<Translation> {
        if (!isDefinedAndNotNull(key) || !key.length) {
            throw new Error(`Parameter "key" required`);
        }

        const reemit$: Observable<unknown> = lang
            ? merge(
                  this.onLangChange,
                  this.chainTranslationChange$().pipe(filter((e) => e.lang === lang)),
              )
            : this.onLangChange;

        return concat(
            defer(() => this.get(key, interpolateParams, lang)),
            reemit$.pipe(
                switchMap(() => {
                    const res = this.getParsedResult(key, interpolateParams, lang);
                    return makeObservable(res);
                }),
            ),
        );
    }

    /**
     * Returns a translation instantly from the internal state of loaded translation.
     * All rules regarding the current language, the preferred language of even fallback languages
     * will be used except any promise handling.
     *
     * When `lang` is provided, the lookup goes directly to the specified language,
     * bypassing the current language and fallback chain.
     */
    public instant(
        key: string | string[],
        interpolateParams?: InterpolationParameters,
        lang?: Language,
    ): Translation {
        if (!isDefinedAndNotNull(key) || key.length === 0) {
            return "";
        }

        if (lang && !this.hasTranslationInChain(lang)) {
            this.warnUnloadedInstantLang(lang);
        }

        const result = this.getParsedResult(key, interpolateParams, lang);

        return isObservable(result) ? this.keyToObject(key) : result;
    }

    private warnedUnloadedInstantLangs = new Set<Language>();
    private warnUnloadedInstantLang(lang: Language): void {
        // Delegate to the root so the warn budget is shared across the isolated
        // subtree. With parent === null at isolated boundaries, getRoot() stops
        // at the right place — one warn per (isolated subtree, lang) pair.
        const root = this.getRoot();
        if (root !== this) {
            root.warnUnloadedInstantLang(lang);
            return;
        }
        if (this.warnedUnloadedInstantLangs.has(lang)) return;
        // instant() runs inside the translate() computed; keep the Set write and
        // console.warn out of the reactive graph so they never register as a
        // producer during signal evaluation.
        untracked(() => {
            this.warnedUnloadedInstantLangs.add(lang);
            console.warn(
                `@ngx-translate/core: instant() called with lang="${lang}" but no ` +
                    `translations are loaded for that language. Returning the key as ` +
                    `fallback. Load with use("${lang}") or setTranslation("${lang}", ...) first.`,
            );
        });
    }

    /**
     * Checks whether a translation exists for the given key, without invoking
     * the missing-translation handler and without interpolating.
     *
     * Resolution mirrors `instant()`: with `lang`, the lookup goes directly to
     * the specified language (and up the parent service chain); without, it
     * falls back to the current language, then the fallback language, then up
     * the parent chain.
     *
     * An empty string counts as an existing translation; an explicit `null`
     * counts as missing (both match `instant()` / `get()` resolution).
     *
     * @returns true if the key resolves to a translation, false for a missing,
     * empty, or undefined key
     */
    public hasTranslationKey(key: string, lang?: Language): boolean {
        if (!isDefinedAndNotNull(key) || key.length === 0) {
            return false;
        }

        return isDefinedAndNotNull(this.getTextToInterpolate(key, lang));
    }

    /**
     * Returns a Signal that provides the translated value and automatically
     * updates when the language changes or translations are reloaded.
     *
     * Parameters accept plain values or arrow functions. Signal reads inside
     * the function are tracked reactively. Signals themselves are also
     * accepted directly, since Signal<T> is callable.
     *
     * @param key The translation key (or array of keys), a function returning one
     * @param params Optional interpolation parameters, or a function returning them
     * @param lang Optional language override, or a function returning one
     * @returns A Signal that emits the translated value(s)
     *
     * @example
     * // Static key
     * greeting = this.translate.translate('HELLO');
     *
     * @example
     * // Derived key from another signal (no separate computed needed)
     * model = signal({ currentKey: 'HELLO' });
     * greeting = this.translate.translate(() => this.model().currentKey);
     *
     * @example
     * // Multi-key lookup
     * labels = this.translate.translate(['SAVE', 'CANCEL']);
     */
    public translate(
        key: string | string[] | (() => string | string[]),
        params?: InterpolationParameters | (() => InterpolationParameters | undefined),
        lang?: Language | (() => Language | undefined),
    ): Signal<Translation | TranslationObject> {
        return computed(() => {
            const currentKey = typeof key === "function" ? key() : key;
            const currentParams = typeof params === "function" ? params() : params;
            const currentLang = typeof lang === "function" ? lang() : lang;

            return this.instant(currentKey, currentParams, currentLang);
        });
    }

    protected keyToObject(key: string | string[]) {
        if (Array.isArray(key)) {
            return key.reduce((acc: Record<string, string>, currKey: string) => {
                acc[currKey] = currKey;
                return acc;
            }, {});
        }
        return key;
    }

    /**
     * Sets the translated value of a key, after compiling it
     */
    public set(key: string, translation: string | TranslationObject, lang?: Language): void {
        // Void mutator: it writes the store's `translations` signal. The
        // current value is read back (via getTranslations) and the language is
        // resolved (via getCurrentLang) to merge the new key in. Run the whole
        // body in `untracked` so those reads never register as dependencies of
        // a caller's reactive context. Defensive (issue #1633): like
        // store.setTranslations this does not self-retrigger an effect today
        // (the store re-reads translations after its write), but its reads run
        // here in the caller's context — outside the store's own untracked — so
        // set() carries its own wrap to keep the invariant locally.
        untracked(() => {
            const targetLang = lang ?? this.getCurrentLang()!;
            this.store.setTranslations(
                targetLang,
                insertValue(
                    this.store.getTranslations(targetLang),
                    key,
                    isString(translation)
                        ? this.compiler.compile(translation, targetLang)
                        : this.compiler.compileTranslations(translation, targetLang),
                ),
                false,
            );
        });
    }

    /**
     * Allows reloading the lang file from the file
     */
    public reloadLang(lang: Language): Observable<InterpolatableTranslationObject> {
        // Command, not a reactive read — run the synchronous body in `untracked`
        // so resetLang's store/registry writes and loadAndCompileTranslations'
        // registry read+write do not leak into a caller's reactive context
        // (issue #1633).
        return untracked(() => {
            this.resetLang(lang);
            return this.loadAndCompileTranslations(lang);
        });
    }

    /**
     * Deletes stored translations for `lang` and clears the in-flight registry
     * entry — `isLoading()` flips to `false` immediately on this service.
     *
     * Does NOT cancel the underlying network call: if the loader is mid-flight
     * when this method returns, the request can still complete and `tap()`
     * translations back into the store. To replace state and re-fetch
     * deterministically, follow with `reloadLang(lang)`.
     */
    public resetLang(lang: Language): void {
        // Unconditional clear — `resetLang`'s contract is "forget this entry
        // NOW", regardless of which load owns it.
        this.loadingTranslations.clear(lang);
        this.store.deleteTranslations(lang);
    }

    /**
     * Deletes the language `lang` entirely: removes its translations from the
     * store, unregisters it from `getLangs()`, and clears any in-flight load
     * entry — `isLoading()` flips to `false` immediately on this service.
     *
     * This restores the pre-v18 escape hatch `delete translate.translations[lang]`,
     * which left the language registered in `getLangs()`.
     *
     * If `lang` is the currently used language, the current-language pointer is
     * kept: it is not reset and no `onLangChange` is emitted, matching the old
     * `delete` workaround. Removing stored translations emits
     * `onTranslationChange` with an empty translation object so subscribers
     * refresh through the usual parent, fallback, or missing-translation lookup.
     *
     * All earlier loads and retries for this language owned by this service
     * are unsubscribed. Late responses cannot restore it
     * or apply a delayed current/fallback language change. Other services are
     * unaffected, and a subsequent `use()` or `reloadLang()` can load it again.
     * A child also ignores automatic loads from ancestor language requests
     * started before deletion; later explicit requests can load it again.
     * Subscribers to a cancelled load complete without another value or error.
     * Completed load results remain readable. Pending lookups resolve through
     * the usual parent, fallback, or missing-translation handling. Current and
     * fallback language pointers are kept.
     */
    public deleteLanguage(lang: Language): void {
        untracked(() => {
            this.deletedLanguageRequests.set(lang, this.getRoot().languageRequestSequence);
            const cancellation = this.loadCancellation.get(lang);
            this.loadCancellation.delete(lang);
            this.loadingTranslations.clear(lang);
            this.store.removeLanguages([lang]);
            this.store.deleteTranslations(lang);
            // Notify after deletion so completion callbacks can safely start a fresh load.
            cancellation?.next();
            cancellation?.complete();
        });
    }

    /**
     * Returns the language code name from the browser, e.g. "de"
     */
    public static getBrowserLang(): Language | undefined {
        if (typeof window === "undefined" || !window.navigator) {
            return undefined;
        }

        const browserLang = this.getBrowserCultureLang();

        return browserLang ? browserLang.split(/[-_]/)[0] : undefined;
    }

    /**
     * Returns the culture language code name from the browser, e.g. "de-DE"
     */
    public static getBrowserCultureLang(): Language | undefined {
        if (typeof window === "undefined" || typeof window.navigator === "undefined") {
            return undefined;
        }

        return window.navigator.languages
            ? window.navigator.languages[0]
            : window.navigator.language ||
                  window.navigator.browserLanguage ||
                  window.navigator.userLanguage;
    }

    public getBrowserLang(): Language | undefined {
        return TranslateService.getBrowserLang();
    }

    public getBrowserCultureLang(): Language | undefined {
        return TranslateService.getBrowserCultureLang();
    }

    /**
     * The current language as a reactive Signal.
     * Use `getCurrentLang()` for a non-reactive snapshot.
     */
    get currentLang(): Signal<Language | null> {
        return this.isRoot ? this._currentLang.asReadonly() : this.parent!.currentLang;
    }

    /**
     * The fallback language as a reactive Signal.
     * Use `getFallbackLang()` for a non-reactive snapshot.
     */
    get fallbackLang(): Signal<Language | null> {
        return this.isRoot ? this._fallbackLang.asReadonly() : this.parent!.fallbackLang;
    }

    /** The ordered fallback chain as a reactive Signal. */
    get fallbackLangs(): Signal<readonly Language[]> {
        return this.isRoot ? this._fallbackLangs : this.parent!.fallbackLangs;
    }
}
